import React from 'react';
import AppHeader from '../components/layout/AppHeader';
import NewsletterSignup from '../components/common/NewsletterSignup';

const features = [
    {
        icon: '📋',
        title: 'Dispatch — Global Task Marketplace',
        description:
            'Post and apply for tasks across development, design, writing, and more. Get matched with vetted talent or find work globally — all payments are secured through the platform.',
    },
    {
        icon: '🎓',
        title: 'Academy — Learn & Upskill',
        description:
            'Access thousands of curated courses from Coursera, edX, MIT OpenCourseWare, and more — all in one place. Build skills that unlock higher-paying opportunities on Dispatch.',
    },
    {
        icon: '🌐',
        title: 'Virtual Workspace — Collaborate Anywhere',
        description:
            'Real-time 3D team rooms, shared documents, video, and voice — your team\'s virtual office, accessible from anywhere in the world.',
    },
    {
        icon: '💳',
        title: 'Cross-Border Payments',
        description:
            'Get paid in your local currency via mobile money, bank transfer, or crypto. No more payment friction for African and global freelancers.',
    },
    {
        icon: '🏆',
        title: 'Talent Hub — Build Your Reputation',
        description:
            'Showcase your skills, portfolio, and project history. Get endorsed by clients and peers. Your TechUnity profile is your professional passport.',
    },
    {
        icon: '🤖',
        title: 'UnityBot — Smart Automation',
        description:
            'Our built-in AI assistant helps you manage tasks, answer questions, and surface relevant opportunities — available 24/7 right inside the platform.',
    },
];

const stats = [
    { value: '50+', label: 'Countries Represented' },
    { value: '1,000+', label: 'Platform Members' },
    { value: '500+', label: 'Courses Available' },
    { value: '$0', label: 'Cost to Join' },
];

const LaunchPost: React.FC = () => {
    const publishDate = 'February 19, 2026';

    return (
        <div className="min-h-screen bg-premium-wood-dark text-white font-sans">
            <AppHeader title="We're Live!" />

            <main className="container mx-auto px-4 py-12 max-w-4xl">
                {/* Badge */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 bg-premium-gold/20 border border-premium-gold/40 rounded-full px-5 py-2 text-premium-gold font-semibold text-sm mb-6 animate-pulse">
                        🚀 Official Launch Announcement
                    </div>
                    <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-4 leading-tight">
                        TechUnity Global<br />
                        <span className="text-premium-gold">Is Now Live</span>
                    </h1>
                    <p className="text-white/50 text-sm mb-8">
                        Published: <span className="text-premium-gold">{publishDate}</span>
                    </p>
                    <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
                        After months of building, testing, and refining — we're officially open.
                        TechUnity Global is the all-in-one operating system for global talent:
                        find work, learn new skills, collaborate in real time, and get paid — anywhere in the world.
                    </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
                    {stats.map((stat) => (
                        <div
                            key={stat.label}
                            className="bg-black/30 border border-premium-gold/20 rounded-xl p-6 text-center"
                        >
                            <div className="text-3xl font-bold text-premium-gold">{stat.value}</div>
                            <div className="text-white/60 text-sm mt-1">{stat.label}</div>
                        </div>
                    ))}
                </div>

                {/* The Story */}
                <section className="mb-16 bg-black/20 border border-white/5 rounded-2xl p-8 md:p-12">
                    <h2 className="text-2xl font-serif font-bold text-premium-gold mb-6">Why We Built This</h2>
                    <div className="space-y-4 text-white/80 leading-relaxed">
                        <p>
                            We built TechUnity Global because we saw a fundamental gap: talented professionals
                            across Africa and beyond were being left out of the global digital economy — not because
                            of their skill, but because of payment barriers, lack of visibility, and no unified
                            platform designed with them in mind.
                        </p>
                        <p>
                            Most freelance platforms are built for Western markets, charge high fees, and make
                            cross-border payment nearly impossible. We set out to change that.
                        </p>
                        <p>
                            TechUnity Global is the platform we wished existed: a full operating system where you
                            can find work, upskill, collaborate with global teams, and get paid securely — all in
                            one place, designed for global talent from day one.
                        </p>
                    </div>
                </section>

                {/* Features */}
                <section className="mb-16">
                    <h2 className="text-2xl font-serif font-bold text-white mb-8">
                        What's in the Platform
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {features.map((feature) => (
                            <div
                                key={feature.title}
                                className="bg-black/20 border border-premium-gold/10 hover:border-premium-gold/30 rounded-xl p-6 transition-colors"
                            >
                                <div className="text-3xl mb-3">{feature.icon}</div>
                                <h3 className="font-bold text-premium-gold mb-2">{feature.title}</h3>
                                <p className="text-white/65 text-sm leading-relaxed">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* What's Next */}
                <section className="mb-16 bg-gradient-to-br from-premium-gold/10 to-transparent border border-premium-gold/20 rounded-2xl p-8 md:p-12">
                    <h2 className="text-2xl font-serif font-bold text-premium-gold mb-6">What's Coming Next</h2>
                    <div className="space-y-3 text-white/80">
                        {[
                            'Mobile app (iOS & Android) — Q2 2026',
                            'Escrow & milestone-based payments — Q2 2026',
                            'AI-powered talent matching — Q3 2026',
                            'Company & team accounts — Q3 2026',
                            'Certificate & credential system — Q4 2026',
                        ].map((item) => (
                            <div key={item} className="flex items-start gap-3">
                                <span className="text-premium-gold mt-0.5">→</span>
                                <span className="text-sm">{item}</span>
                            </div>
                        ))}
                    </div>
                </section>

                {/* CTA */}
                <section className="text-center mb-16">
                    <h2 className="text-2xl font-serif font-bold text-white mb-4">Join the Movement</h2>
                    <p className="text-white/70 mb-8">
                        It's free to join. No credit card required.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="/#/register"
                            className="bg-premium-gold text-premium-wood-dark font-bold px-8 py-4 rounded-xl hover:opacity-90 transition-opacity text-lg"
                        >
                            Create Free Account →
                        </a>
                        <a
                            href="/#/talent"
                            className="border border-premium-gold/40 text-premium-gold font-bold px-8 py-4 rounded-xl hover:bg-premium-gold/10 transition-colors text-lg"
                        >
                            Browse Talent
                        </a>
                    </div>
                </section>

                {/* Newsletter */}
                <NewsletterSignup variant="banner" />
            </main>
        </div>
    );
};

export default LaunchPost;
